<template>
	<v-sheet rounded>
		<v-card outlined color="#F9F9FB">
			<v-card-title class="align-center">
				<v-icon left large color="#7eb440">mdi-office-building-marker</v-icon>
				<h3 class="grey--text text--darken-3">{{ title }}</h3>
				<v-spacer />
				<slot />
			</v-card-title>

			<v-divider class="thick" />

			<v-data-table
				v-model="selected"
				:headers="tableHeaders"
				:items="properties"
				:loading="loading"
				:search="searchProperties"
				show-select
				disable-pagination
				hide-default-footer
				class="table-stripped"
				@update:page="$vuetify.goTo(0)">
				<template #top>
					<v-toolbar flat>
						<v-menu
							offset-y
							close-on-click
							close-on-content-click>
							<template #activator="{ on, attrs }">
								<v-btn
									color="primary"
									x-small
									elevation="0"
									:disabled="! length(selected)"
									class="mr-2"
									v-bind="attrs"
									v-on="on">
									Create Report
									<v-icon right>mdi-chevron-down</v-icon>
								</v-btn>
							</template>
							<v-list
								nav
								density="compact"
								class="tnm-v-list">
								<v-list-item
									v-for="(item, i) in reportTypes"
									:key="i"
									:value="item"
									@click="addReport(item.id)">
									<v-list-item-title v-text="item.report_type"></v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
						<delete-button
							v-if="auth.user_type === 1"
							:x-small="true"
							:classes="'pl-1 pr-1'"
							:disabled="! length(selected)"
							model="properties"
							:model-ids="selected"
							warning="This cannot be undone. All data, and related reports will be deleted."
							:only="['properties','reports']"
							@deleted="selected = []">
							<v-icon>mdi-delete-forever</v-icon> <span v-if="length(selected)">({{ length(selected) }})</span>
						</delete-button>
						<v-spacer />
						<div class="flex-fill datatable-filter-large">
							<v-text-field
								v-model.trim="searchProperties"
								class="table-search"
								append-icon="mdi-magnify"
								placeholder="Search..."
								name="search"
								autocomplete="off"
								single-line
								clearable
								filled
								outlined
								dense
								full-width
								autofocus
								hide-details />
						</div>
						<div class="ml-3">
							<v-chip>{{ length(properties) | numeric }}</v-chip>
						</div>
					</v-toolbar>
				</template>

				<template #item.full_address="{ item }">
					{{ item.full_address }}
				</template>

				<template #item.asset_relationship="{ item }">
					{{ item.asset_relationship | capitalize }}
				</template>

				<template #item.laborer="{ item }">
					<v-edit-dialog
						v-if="auth.user_type === 1 || auth.user_type === 2"
						persistent
						large
						:return-value.sync="item.laborer[0]"
						@save="updateLabor(item)">
						<span v-if="!item.laborer[0]" class="grey--text">
							Not Assigned
						</span>
						<span v-else class="text-no-wrap mx-1">
							{{ item.laborer[0].first_name }} {{ item.laborer[0].last_name }}
						</span>
						<template #input>
							<v-autocomplete
								v-model="item.laborer[0]"
								:items="laborers"
								item-text="name"
								return-object
								hide-details
								placeholder="Select Laborer"
								single-line />
						</template>
					</v-edit-dialog>
					<span v-else class="text-no-wrap mx-1">
						{{ item.laborer[0].first_name }} {{ item.laborer[0].last_name }}
					</span>
				</template>

				<template #item.status="{ item }" :class="item.status.color">
					<td :class="item.status.color" class="status text-center">
						<v-edit-dialog
							persistent
							large
							:return-value.sync="item.status"
							@save="updateStatus(item)">
							<span class="status text--darken-4" :class="item.status.color.split(' ')[0] + '--text'">{{ item.status.name }}</span>
							<template #input>
								<v-autocomplete
									v-model="item.status"
									:items="statuses"
									item-text="name"
									return-object
									hide-details
									placeholder="Select Status"
									single-line />
							</template>
						</v-edit-dialog>
					</td>
				</template>

				<template #item.actions="{ item }" class="actions-dots">
					<v-menu
						bottom
						left
						allow-overflow
						min-width="235">
						<template #activator="{ on, attrs }">
							<v-btn
								icon
								color="#444"
								v-bind="attrs"
								v-on="on">
								<v-icon>mdi-dots-vertical</v-icon>
							</v-btn>
						</template>

						<v-list dense>
							<v-list-item dense link href="#">
								<v-dialog v-model="dialogEdit" transition="scroll-x-reverse-transition" max-width="1100">
									<template #activator="{ on, attrs }">
										<v-list-item-icon>
											<v-icon color="secondary">
												mdi-pencil
											</v-icon>
										</v-list-item-icon>
										<v-list-item-content>
											<v-list-item-title
												model="properties"
												:model-id="item.id"
												v-bind="attrs"
												v-on="on">
												Edit Property
											</v-list-item-title>
										</v-list-item-content>
									</template>
									<template #default>
										<v-card>
											<v-card-title>
												<div>Edit Property</div>
												<v-icon right class="ml-auto theme--dark" @click="dialogEdit = false">mdi-close</v-icon>
											</v-card-title>
											<v-divider class="mb-5" />
											<v-card-text>
												<property-form :property="item" @success="dialogEdit = false"></property-form>
											</v-card-text>
										</v-card>
									</template>
								</v-dialog>
							</v-list-item>
							<Link :href="route('properties.data', item)" class="v-list-item v-list-item--dense v-list-item--link theme--light">
								<div class="v-list-item__icon"><v-icon color="primary">mdi-file-document-edit</v-icon></div>
								<div class="v-list-item__content"><div class="v-list-item__title">Report Writer Data</div></div>
							</Link>
							<v-divider v-if="auth.user_type === 1" class="mt-2"></v-divider>
							<delete-button
								v-if="auth.user_type === 1 || auth.user_type === 2"
								list-item
								model="properties"
								:model-id="item.id"
								:only="['properties','reports']"
								warning="This cannot be undone. All data, and related reports will be deleted.">
								Delete Property
							</delete-button>
						</v-list>
					</v-menu>
				</template>
			</v-data-table>

			<SendLaborAssignment v-model="laborAssignmentDialog"
				:project="project"
				:property="assignedProperty"
				:laborer="laborAssignmentModel" />
		</v-card>
	</v-sheet>
</template>
<script>
import api from '@/api'
import DeleteButton from '@/Pages/Backend/Components/DeleteButton'
import PropertyForm from '@/Pages/Backend/Properties/Form'
import SendLaborAssignment from '@/Pages/Backend/Components/Dialogs/SendLaborAssignment.vue'

export default {
	components: { DeleteButton, PropertyForm, SendLaborAssignment },
	props: {
		title: {
			type: String,
			default: 'Properties'
		},
		properties: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		project: {
			type: [Array, Object],
			default: () => {},
			required: true
		}
	},
	data () {
		return {
			searchProperties: '',
			statuses: [],
			laborers: [],
			loading: false,
			selected: [],
			laborAssignmentDialog: false,
			laborAssignmentModel: {},
			assignedProperty: {},
			sendingAssignment: false,
			auth: this.$page.props.auth,
			reportTypes: this.$page.props.reportTypes,
			laborAssignment: this.$inertia.form({
				subject: 'Bell Oldow Environmental Job Assignment - Please Respond ASAP',
				message: ''
			}),
			tableHeaders: [
				{
					text: 'ID',
					sortable: true,
					value: 'id'
				},
				{
					text: 'Address',
					sortable: false,
					align: 'start',
					value: 'full_address'
				},
				{
					text: 'Asset Relationship',
					sortable: false,
					align: 'start',
					value: 'asset_relationship'
				},
				{
					text: 'Laborer',
					sortable: false,
					align: 'center',
					value: 'laborer'
				},
				{
					text: 'Status',
					sortable: false,
					align: 'center',
					value: 'status'
				},
				{
					text: '',
					sortable: false,
					align: 'center',
					value: 'actions'
				}
			],
			dialogEdit: false
		}
	},
	watch: {
		//
	},
	mounted () {
		this.getPropertyStatuses()
		this.getLaborers()
	},
	methods: {
		getPropertyStatuses () {
			const method = 'GET'
			const data = {}

			const url = this.route('property-statuses.api.index')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.statuses = response.data
			})
		},
		getLaborers () {
			const method = 'GET'
			const data = {}

			const url = this.route('users.api.laborers')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.laborers = response.data
			})
		},
		updateStatus (property) {
			this.loading = true
			property.property_status_id = property.status.id

			const route = this.route('properties.update', { project: property.project_id, property: property.id })

			this.$inertia.put(route, property, {
				only: ['properties', 'flash'],
				preserveScroll: true,
				preserveState: true,
				onFinish: () => this.loading = false
			})
		},
		updateLabor (property) {
			this.loading = true

			const route = this.route('properties.update.labor', { property: property.id })

			this.$inertia.put(route, property, {
				only: ['projects', 'flash'],
				preserveScroll: true,
				preserveState: true,
				onFinish: () => this.loading = false
			})

			if (property.hasOwnProperty('laborer') && this.length(property.laborer)) {
				this.laborAssignmentDialog = true
				this.laborAssignmentModel = property.laborer[0]
				this.assignedProperty = property
			}
		},
		addReport (reportTypeId) {
			this.loading = true

			const route = this.route('reports.create', { project_id: this.project.id, report_id: reportTypeId })

			this.$inertia.post(route, {
				'properties': this.selected
			}, {
				only: ['reports', 'flash'],
				preserveScroll: true,
				preserveState: true,
				onFinish: () => {
					this.loading = false
					this.selected = []
				}
			})
		}
	}
}
</script>
